import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import {
  Music, Mic, AudioWaveform, Megaphone, Headphones, Phone, Mail, Globe,
  MessageSquare, PenLine, SlidersHorizontal, Check, Radio, Play,
} from "lucide-react";

const PROCESS = [
  { icon: MessageSquare, title: "Discover", text: "We learn about your brand, goals and vision." },
  { icon: PenLine, title: "Create", text: "We craft lyrics, melodies and arrangements." },
  { icon: SlidersHorizontal, title: "Produce", text: "We produce a high-quality recording that brings your song to life." },
  { icon: Check, title: "Refine", text: "We fine-tune every detail until it's perfect." },
  { icon: Radio, title: "Deliver", text: "You get a ready-to-use song that elevates your brand." },
];

const SOLUTIONS = [
  { icon: Music, title: "Jingles", text: "Short, catchy and unforgettable." },
  { icon: Mic, title: "Corporate Theme Songs", text: "Powerful anthems that represent who you are." },
  { icon: AudioWaveform, title: "Brand Soundtracks", text: "Background music that enhances your brand." },
  { icon: Megaphone, title: "Brand Promotion Through Music", text: "Music that amplifies your message and mission." },
  { icon: Headphones, title: "Full-Service Production", text: "From concept to final mix, we handle it all." },
];

const CLIENTS = [
  "SUMMIT FINANCIAL GROUP", "WELLCARE HEALTH SOLUTIONS", "GREENLEAF MARKET",
  "BITE BURGER CO.", "DRIVEN MOTORS", "NEXUS TECHNOLOGIES",
];

function SampleCard({ sample }) {
  return (
    <div className="card group overflow-hidden flex flex-col" data-testid={`sample-card-${sample.id}`}>
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={sample.image_url}
          alt={sample.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="absolute top-3 left-3 text-[10px] tracking-[0.25em] uppercase text-gold bg-black/70 border border-gold/40 px-2 py-1">
          {sample.industry}
        </div>
      </div>
      <div className="p-5 flex flex-col gap-3 flex-1">
        <h3 className="font-heading text-lg text-white">{sample.title}</h3>
        <p className="text-white/60 text-sm line-clamp-2">{sample.description}</p>
        <audio
          controls
          preload="none"
          src={sample.audio_url}
          className="mt-auto"
          data-testid={`sample-audio-${sample.id}`}
        />
      </div>
    </div>
  );
}

export default function Home() {
  const { settings } = useOutletContext();
  const [samples, setSamples] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All");

  useEffect(() => {
    api.get("/samples").then((r) => setSamples(r.data)).catch(() => {});
  }, []);

  const industries = ["All", ...new Set(samples.map((s) => s.industry))];
  const filtered = activeFilter === "All" ? samples : samples.filter((s) => s.industry === activeFilter);

  return (
    <div className="bg-black text-white">
      {/* ============= HERO ============= */}
      <section className="hero-backdrop relative overflow-hidden">
        <div className="container-x pt-6 md:pt-8 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10 items-start">
          {/* Left */}
          <div className="lg:col-span-7 animate-fade-in-up pt-2 lg:pt-6 flex flex-col items-center text-center">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-14 h-14 border border-gold flex items-center justify-center bg-black/60 shrink-0 mt-2">
                <Music className="text-gold" size={26} />
              </div>
              <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold leading-none">
                <span className="text-white">Song</span><span className="text-gold">niture</span>
              </h1>
            </div>
            <p className="text-[13px] md:text-sm tracking-[0.25em] uppercase text-white/80 font-semibold mb-5">
              We Turn <span className="text-gold">Brands</span> Into <span className="text-gold">Songs</span> That <span className="text-gold">Resonate</span>.
            </p>
            <div className="flex items-center gap-3 text-[11px] tracking-[0.25em] uppercase text-white/50 mb-6">
              <span>Jingles</span><span className="text-gold">|</span>
              <span>Theme Songs</span><span className="text-gold">|</span>
              <span>Brand Soundtracks</span>
            </div>
            {/* Thin gold divider line */}
            <div className="h-px w-full max-w-[640px] bg-gold/60 mb-10" />
            {/* Centered subheadline */}
            <div className="max-w-[640px] mb-10">
              <p className="text-white/85 text-xl md:text-2xl text-center leading-relaxed font-light">
                {settings?.hero_subheadline || "Original music that connects, inspires and leaves a lasting impact."}
              </p>
            </div>
            <div className="flex flex-wrap gap-4 mb-12 justify-center">
              <a href="#samples" data-testid="hero-listen-samples" className="btn-gold">
                <Play size={16} /> Listen to Samples
              </a>
              <Link to="/contact" data-testid="hero-create-song" className="btn-outline">
                Let's Create Your Song
              </Link>
            </div>

            {/* Mini icons row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-2xl">
              {[
                { icon: Music, label: "CATCHY JINGLES", sub: "THAT STICK" },
                { icon: Mic, label: "POWERFUL THEMES", sub: "THAT INSPIRE" },
                { icon: AudioWaveform, label: "SOUNDTRACKS THAT", sub: "ELEVATE BRANDS" },
                { icon: Headphones, label: "MUSIC THAT BUILDS", sub: "BRAND LOYALTY" },
              ].map((it) => (
                <div key={it.label} className="flex flex-col items-center text-center gap-2">
                  <it.icon className="text-gold" size={28} />
                  <div className="text-[10px] tracking-[0.18em] text-white/80 font-semibold leading-tight">
                    {it.label}<br />{it.sub}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Founder */}
          <div className="lg:col-span-5 relative animate-fade-in">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-br from-gold/30 via-transparent to-gold-dark/20 blur-2xl opacity-50" />
              <div className="relative border border-gold/40 overflow-hidden">
                <img
                  src={settings?.founder_photo || "/perry-frazier.jpeg"}
                  alt="Perry Frazier - Founder"
                  className="w-full object-cover"
                  data-testid="founder-photo"
                />
              </div>
            </div>
            <div className="mt-6 flex items-end justify-between gap-4">
              <div className="text-left">
                <div className="font-heading text-2xl text-gold uppercase tracking-wide">{settings?.founder_name || "Perry Frazier"}</div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-white/60 font-semibold mt-1">
                  {settings?.founder_title || "Founder / Producer"}
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end text-white/85 text-sm">
                  <Mail size={14} className="text-gold" /> {settings?.email}
                </div>
                <div className="flex items-center gap-2 justify-end text-white/85 text-sm mt-2">
                  <Globe size={14} className="text-gold" /> {settings?.website}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="gold-divider" />

      {/* ============= SOLUTIONS ============= */}
      <section className="section bg-ink-900">
        <div className="container-x">
          <div className="text-center mb-16">
            <div className="eyebrow mb-3">— Solutions —</div>
            <h2 className="font-heading text-3xl md:text-4xl tracking-wide">
              CUSTOM MUSIC SOLUTIONS FOR EVERY BRAND
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-10">
            {SOLUTIONS.map((s, i) => (
              <div key={s.title} className="text-center" data-testid={`solution-${i}`}>
                <s.icon className="text-gold mx-auto mb-5" size={42} strokeWidth={1.4} />
                <h3 className="text-gold font-heading text-lg mb-2 uppercase tracking-wide">{s.title}</h3>
                <p className="text-white/65 text-sm leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============= SAMPLES ============= */}
      <section id="samples" className="section bg-black">
        <div className="container-x">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl tracking-wide mb-3">SAMPLE THEME SONGS</h2>
            <p className="text-white/60">Real brands. Original music. Real impact.</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
            {industries.map((ind) => (
              <button
                key={ind}
                onClick={() => setActiveFilter(ind)}
                data-testid={`filter-${ind.replace(/\s|&/g, "_")}`}
                className={`px-5 py-2 text-[11px] tracking-[0.22em] uppercase font-semibold border transition-all ${
                  activeFilter === ind
                    ? "bg-gold text-black border-gold"
                    : "border-gold/40 text-white/80 hover:border-gold hover:text-gold"
                }`}
              >
                {ind}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((s) => <SampleCard key={s.id} sample={s} />)}
          </div>
          <div className="mt-12 text-center">
            <Link to="/samples" data-testid="view-more-samples" className="btn-outline">View More Samples</Link>
          </div>
        </div>
      </section>

      <div className="gold-divider" />

      {/* ============= PROCESS ============= */}
      <section className="section bg-ink-900">
        <div className="container-x">
          <div className="text-center mb-20">
            <h2 className="font-heading text-3xl md:text-4xl tracking-wide">OUR CREATIVE PROCESS</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-10 relative">
            {PROCESS.map((step, i) => (
              <div key={i} className="text-center relative" data-testid={`process-step-${i + 1}`}>
                <div className="relative inline-flex">
                  <div className="w-14 h-14 rounded-full bg-black border-2 border-gold flex items-center justify-center text-gold font-heading text-xl font-bold shadow-gold">
                    {i + 1}
                  </div>
                  {i < PROCESS.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 left-full w-[calc(100%+1rem)] h-px border-t border-dashed border-gold/50" />
                  )}
                </div>
                <step.icon className="text-gold mx-auto my-4" size={36} strokeWidth={1.4} />
                <h3 className="text-white font-semibold uppercase text-sm tracking-[0.18em] mb-2">{step.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============= CLIENTS ============= */}
      <section className="py-20 bg-black border-y border-ink-700">
        <div className="container-x">
          <div className="text-center text-[11px] tracking-[0.3em] uppercase text-gold font-semibold mb-10">
            Trusted By Brands That Value The Power Of Music
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 items-center">
            {CLIENTS.map((c) => (
              <div key={c} className="text-center text-white/60 font-heading tracking-wide text-sm hover:text-gold transition-colors">
                {c}
              </div>
            ))}
          </div>
          <div className="mt-14 max-w-3xl mx-auto text-center">
            <div className="text-gold text-3xl font-script mb-2">"</div>
            <p className="text-white/80 italic text-lg leading-relaxed">
              The song Songniture created for our brand became an instant connection with our customers. It's more than music — it's our identity.
            </p>
            <div className="mt-4 text-[11px] tracking-[0.25em] uppercase text-gold font-semibold">— Summit Financial Group</div>
          </div>
        </div>
      </section>

      {/* ============= CTA FOOTER ============= */}
      <section className="section bg-ink-900">
        <div className="container-x text-center">
          <h2 className="font-heading text-3xl md:text-5xl mb-4">
            READY TO TURN YOUR BRAND INTO A SONG THAT RESONATES?
          </h2>
          <p className="text-white/65 max-w-2xl mx-auto mb-10">
            Let's create something memorable together. Your story. Your sound. Your legacy.
          </p>
          <Link to="/contact" data-testid="cta-lets-talk" className="btn-gold">Let's Talk →</Link>
        </div>
      </section>
    </div>
  );
}
